// Form rules for a repository, apart from the component that draws them.
// They mirror data/schemas/repository.js and data/models/project/repository.js.

export const NAME_MAX = 120;
export const REMOTE_MAX = 300;
export const BRANCH_MAX = 80;

export const REPOSITORY_KIND_OPTIONS = [
    { value: 'backend',  label: 'Backend' },
    { value: 'frontend', label: 'Frontend' },
    { value: 'admin',    label: 'Panel' },
    { value: 'mobile',   label: 'Móvil' },
    { value: 'infra',    label: 'Infraestructura' },
];

export const ENVIRONMENT_KIND_OPTIONS = [
    { value: 'dev',     label: 'Desarrollo' },
    { value: 'staging', label: 'Staging' },
    { value: 'prod',    label: 'Producción' },
];

export const STATUS_OPTIONS = [
    { value: 2, label: 'Activo' },
    { value: 1, label: 'Inactivo' },
];

// Two shapes and only two, because they are the two ways a machine clones.
// The backend answers 422 — not 400 — to anything else, and DERIVES
// `clone_protocol` from this: it says whether the machine will be asked for a
// token or for a key. Which is why the panel never offers a protocol field.
const HTTPS_REMOTE = /^https:\/\/\S+$/;
const SSH_REMOTE = /^[\w.-]+@[\w.-]+:\S+$/;

export function protocolOf(remote) {
    const value = (remote || '').trim();
    if (HTTPS_REMOTE.test(value)) return 'https';
    if (SSH_REMOTE.test(value)) return 'ssh';
    return null;
}

export const EMPTY_VALUES = {
    project_id: '',
    name: '',
    kind: 'backend',
    remote: '',
    main_branch: 'main',
    status: 2,
};

export function valuesFrom(repository) {
    return {
        project_id: repository?.project_id != null ? String(repository.project_id) : '',
        name: repository?.name ?? '',
        kind: repository?.kind ?? 'backend',
        remote: repository?.remote ?? '',
        main_branch: repository?.main_branch ?? 'main',
        status: repository?.status ?? 2,
    };
}

export function validate(values, { withProject }) {
    const errors = {};
    const name = values.name.trim();
    const remote = values.remote.trim();

    if (withProject && !values.project_id) errors.project_id = 'El proyecto es requerido';

    if (!name) errors.name = 'El nombre del repositorio es requerido';
    else if (name.length > NAME_MAX) errors.name = `No puede pasar de ${NAME_MAX} caracteres`;

    if (!remote) errors.remote = 'Sin remoto nadie puede clonar el repositorio';
    else if (remote.length > REMOTE_MAX) errors.remote = `No puede pasar de ${REMOTE_MAX} caracteres`;
    else if (!protocolOf(remote)) {
        errors.remote = 'Tiene que ser clonable: https://… o git@servidor:ruta';
    }

    if (values.main_branch.length > BRANCH_MAX) {
        errors.main_branch = `No puede pasar de ${BRANCH_MAX} caracteres`;
    }

    return errors;
}

// PATCH takes neither project_id nor standard_version_id.
//
// The project is not a decision that can be revisited: moving a repo to
// another project would silently invalidate every team scope pointing at it.
// Freezing it to a standard version is a separate, explicit operation.
function common(values) {
    return {
        name: values.name.trim(),
        kind: values.kind,
        remote: values.remote.trim(),
        main_branch: values.main_branch.trim() || 'main',
        status: Number(values.status),
    };
}

export function toCreatePayload(values) {
    return { project_id: Number(values.project_id), ...common(values) };
}

export function toUpdatePayload(values) {
    return common(values);
}

// A deployment is IMMUTABLE: created and listed, never edited or deleted.
// Fixing one is recording another.
export const COMMIT_MIN = 7;
export const COMMIT_MAX = 40;
const COMMIT_HASH = /^[0-9a-f]+$/;

export function validateDeployment(values) {
    const errors = {};
    const hash = values.commit_hash.trim().toLowerCase();

    if (!hash) errors.commit_hash = 'El commit es requerido';
    else if (!COMMIT_HASH.test(hash)) errors.commit_hash = 'Tiene que ser hexadecimal';
    else if (hash.length < COMMIT_MIN) errors.commit_hash = `Al menos ${COMMIT_MIN} caracteres`;
    else if (hash.length > COMMIT_MAX) errors.commit_hash = `No puede pasar de ${COMMIT_MAX}`;

    return errors;
}

// Two steps, and the split is the contract's own: where the repo lives is
// decided once and never again (PATCH refuses project_id), while how it is
// cloned can be corrected any day.
export const CREATE_STEPS = [
    { title: 'Dónde vive', fields: ['project_id', 'kind', 'name'] },
    { title: 'Cómo se clona', fields: ['remote', 'main_branch', 'status'] },
];
